import { IconButton, Tooltip } from '@mui/material';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { MODULE_REGISTRY, openableModules } from '@/modules/registry';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';

/**
 * One click back to the apps grid, immediately before the module switcher
 * (owner's call, 2026-10-05 — it used to follow it).
 *
 * The switcher's menu still offers "All apps" — this is the same destination in
 * one click instead of two, so it deliberately reuses the switcher's own icon
 * (`GridViewRounded`) and its i18n key: two doors, one room, one word for it.
 *
 * It is **not rendered for a viewer with exactly one openable module**, the same
 * count `landingFor` uses to skip the grid on sign-in: for them `/modules` is a
 * single tile, and a button that asks a question with one answer is the thing
 * the apps grid decision already removed. Nothing is cached — `usePermissions`
 * reads the live claims, so the moment a second module is granted (a refreshed
 * token) the button appears mid-session. The menu entry covers the one-module
 * viewer either way, so the grid is never unreachable.
 */
export function AllAppsButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { permissions, isPlatform } = usePermissions();

  const modules = openableModules(MODULE_REGISTRY, { isPlatform, permissions });
  // Not on the grid itself: a button that reloads the page you are looking at.
  if (modules.length < 2 || pathname === paths.modules) return null;

  const label = t('modules.allApps');

  return (
    /*
     * `bottom-start` and not a bare `left`/`right`: MUI's Popper flips the
     * `-start` / `-end` variants under RTL and nothing else, so this is the one
     * placement family that means "inline start" in both directions. A physical
     * value is what opened the collapsed sidebar's tooltip over the content in
     * Arabic, which is why that one reads `theme.direction` instead.
     */
    <Tooltip title={label} placement="bottom-start">
      <IconButton
        onClick={() => navigate(paths.modules)}
        aria-label={label}
        sx={{
          width: 38,
          height: 38,
          color: 'text.secondary',
          '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
        }}
      >
        <GridViewRoundedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );
}
